import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { clearActiveContactForConfigurator } from '../lib/contactConfigurator';
import { dispatchSessionExpired } from '../lib/authEvents';

const AuthContext = createContext();

/** Auto-logout after this much idle time so stale "logged in" sessions can't silently fail saves. */
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = 'auth_last_activity_at';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const inactivityTimerRef = useRef(null);
  const lastActivityWriteRef = useRef(0);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token and get user info
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });

      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);

      // Get user info
      const userResponse = await axios.get(`${API_URL}/api/auth/me`);
      setUser(userResponse.data);
      try {
        sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }

      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let message = 'Login failed. Please check your credentials.';
      if (!error.response) {
        message = 'Cannot reach the server. Check your connection and try again.';
      } else if (status >= 500) {
        message = 'Server error (API unavailable). Please try again in a moment or contact support.';
      } else if (typeof detail === 'string' && detail.trim()) {
        message = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        message = detail.map((d) => d.msg).join(' ');
      }
      return {
        success: false,
        error: message,
      };
    }
  };

  const logout = useCallback(async ({ sessionExpired = false } = {}) => {
    try {
      if (localStorage.getItem('access_token')) {
        await axios.post(`${API_URL}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      try {
        sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      delete axios.defaults.headers.common['Authorization'];
      clearActiveContactForConfigurator();
      setToken(null);
      setUser(null);
      if (sessionExpired) {
        dispatchSessionExpired();
      }
    }
  }, []);

  // 30-minute inactivity auto-logout (reuses session-expired login dialog)
  useEffect(() => {
    if (!user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return undefined;
    }

    const markActivity = () => {
      const now = Date.now();
      // Throttle sessionStorage writes
      if (now - lastActivityWriteRef.current > 5000) {
        lastActivityWriteRef.current = now;
        try {
          sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
        } catch {
          /* ignore */
        }
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        logout({ sessionExpired: true });
      }, INACTIVITY_TIMEOUT_MS);
    };

    // If the tab was left open overnight, expire immediately on return
    try {
      const stored = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0);
      if (stored > 0 && Date.now() - stored >= INACTIVITY_TIMEOUT_MS) {
        logout({ sessionExpired: true });
        return undefined;
      }
    } catch {
      /* ignore */
    }

    markActivity();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        try {
          const stored = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0);
          if (stored > 0 && Date.now() - stored >= INACTIVITY_TIMEOUT_MS) {
            logout({ sessionExpired: true });
            return;
          }
        } catch {
          /* ignore */
        }
        markActivity();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, logout]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token: new_refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', new_refresh_token);

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);

      return true;
    } catch (error) {
      await logout({ sessionExpired: true });
      return false;
    }
  }, [logout]);

  const refreshAccessTokenRef = useRef(refreshAccessToken);
  refreshAccessTokenRef.current = refreshAccessToken;

  useEffect(() => {
    let refreshPromise = null;

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest || originalRequest._authRetry) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const url = originalRequest.url || '';
        const isAuthRoute =
          url.includes('/api/auth/login')
          || url.includes('/api/auth/refresh')
          || url.includes('/api/auth/logout');

        if (status === 401 && !isAuthRoute && localStorage.getItem('refresh_token')) {
          originalRequest._authRetry = true;

          if (!refreshPromise) {
            refreshPromise = refreshAccessTokenRef.current().finally(() => {
              refreshPromise = null;
            });
          }

          const refreshed = await refreshPromise;
          if (refreshed) {
            const nextToken = localStorage.getItem('access_token');
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${nextToken}`;
            return axios(originalRequest);
          }
        }

        if (status === 401 && !isAuthRoute && user) {
          await logout({ sessionExpired: true });
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, [logout, user]);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshAccessToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
